/**
 * Carbon modal: a headless UI Dialog (focus trap, Escape, scroll lock, a
 * portal) on layer-02 over a 60% black scrim. A pinned header (1.25rem
 * title, optional subtitle, the 48px Close button), an optional
 * ProgressIndicator for multi-step forms, a body that scrolls under the
 * header, and Carbon's footer button set: secondary (gray) left, primary
 * right, 64px tall. Fields inside step up one layer (FIELD_ON_LAYER_02). Full
 * screen below c-md.
 *
 * `danger` (a destructive confirm) re-points the primary button at Coral and
 * makes the dialog an alertdialog: Coral is the blocking-error hue, and an
 * irreversible action is the one place a button takes it. While
 * `primaryAction.loading`, the dialog can't be dismissed (Escape, the scrim
 * and Close are inert), so a request in flight keeps its dialog.
 *
 * Stacking: a Modal (or ConfirmDialog) opened from inside a SidePanel layers
 * over it, as Carbon's modal over a side panel does: both sit at z-60 in
 * headless UI's portal root, and the dialog opened later is appended after
 * the panel, so its scrim covers the panel too (the page behind is dimmed
 * twice). Keep every overlay at z-60: a lower one (a legacy `z-50` dialog)
 * would paint under an open panel.
 *
 * Testing: headless UI portals the dialog after mount, so renderToStaticMarkup
 * sees nothing. SSR-test the content through ModalFrame, or replace Modal in
 * the test with `vi.mock('@/components/carbon/Modal', ...)` rendering
 * ModalFrame inline.
 */
import { Fragment, type ElementType, type MutableRefObject, type ReactNode } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { Button } from './Button'
import { FIELD_ON_LAYER_02 } from './fieldStyles'
import { IconButton } from './IconButton'
import { IconClose } from './icons'
import { ProgressIndicator, type ProgressStep, type StepState } from './ProgressIndicator'

export interface ModalAction {
  label: string
  onClick?: () => void
  disabled?: boolean
  /** Busy: a spinner replaces the icon and the button is disabled. */
  loading?: boolean
  /** "submit" with `form` submits a form in the body. */
  type?: 'button' | 'submit'
  form?: string
}

export type ModalSize = 'xs' | 'sm' | 'md' | 'lg'

/** A step of a multi-step modal. Without `state`, it is derived from `currentStep`. */
export interface ModalStep {
  id: string
  label: ReactNode
  state?: StepState
}

export interface ModalFrameProps {
  title: ReactNode
  subtitle?: ReactNode
  onClose: () => void
  primaryAction?: ModalAction
  secondaryAction?: ModalAction
  danger?: boolean
  steps?: ModalStep[]
  /** The current step's id. */
  currentStep?: string
  /** The title element. Modal passes Dialog.Title so the dialog is named by it. */
  titleAs?: ElementType
  children?: ReactNode
}

export interface ModalProps extends Omit<ModalFrameProps, 'titleAs'> {
  open: boolean
  size?: ModalSize
  /** Where focus lands on open; defaults to the first focusable element (Close). */
  initialFocus?: MutableRefObject<HTMLElement | null>
}

const WIDTH: Record<ModalSize, string> = {
  xs: 'c-md:max-w-[400px]',
  sm: 'c-md:max-w-[480px]',
  md: 'c-md:max-w-[640px]',
  lg: 'c-md:max-w-[864px]',
}

// Re-points the primary button's tokens at Coral, light and dark.
const DANGER =
  '[--interactive:var(--crit-ink)] [--interactive-hover:var(--sev-crit)] [--interactive-active:var(--sev-crit)] [--on-interactive:var(--text-inverse)]'

function stepStates(steps: ModalStep[], currentStep?: string): ProgressStep[] {
  const at = steps.findIndex((s) => s.id === currentStep)
  return steps.map((s, i) => ({
    id: s.id,
    label: s.label,
    state: s.state ?? (at < 0 ? 'incomplete' : i < at ? 'complete' : i === at ? 'current' : 'incomplete'),
  }))
}

const footerButton = (action: ModalAction, kind: 'primary' | 'secondary') => (
  <Button
    kind={kind}
    fullWidth
    type={action.type ?? 'button'}
    form={action.form}
    disabled={action.disabled}
    loading={action.loading}
    onClick={action.onClick}
    className="min-h-16"
  >
    {action.label}
  </Button>
)

/** The modal's content without the dialog shell: header, steps, body and footer. */
export function ModalFrame({
  title,
  subtitle,
  onClose,
  primaryAction,
  secondaryAction,
  danger,
  steps,
  currentStep,
  titleAs: Title = 'h2',
  children,
}: ModalFrameProps) {
  // While the primary action runs, the dialog can't be dismissed: Close is disabled (and Modal ignores Escape and
  // the scrim), so a request in flight never loses the dialog that reports its result.
  const busy = !!primaryAction?.loading
  return (
    <>
      <header className="relative flex-none py-4 pl-4 pr-14">
        <Title className="text-heading font-normal text-text-primary">{title}</Title>
        {subtitle ? <p className="mt-1 text-body text-text-secondary">{subtitle}</p> : null}
        <IconButton label="Close" icon={<IconClose />} onClick={onClose} disabled={busy} className="absolute right-0 top-0" />
      </header>
      {steps?.length ? (
        <div className="flex-none px-4 pb-4">
          <ProgressIndicator steps={stepStates(steps, currentStep)} />
        </div>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-8 pt-2 text-body text-text-primary">{children}</div>
      {primaryAction || secondaryAction ? (
        <footer className={clsx('grid flex-none grid-cols-2 gap-px', danger && DANGER)}>
          {secondaryAction ? footerButton(secondaryAction, 'secondary') : <span />}
          {primaryAction ? footerButton(primaryAction, 'primary') : null}
        </footer>
      ) : null}
    </>
  )
}

export function Modal({ open, onClose, size = 'sm', initialFocus, danger, ...frame }: ModalProps) {
  // Escape and a scrim click reach onClose through Dialog; both are ignored while the primary action is busy.
  const busy = !!frame.primaryAction?.loading
  const dismiss = () => {
    if (!busy) onClose()
  }
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-[60]" onClose={dismiss} initialFocus={initialFocus} role={danger ? 'alertdialog' : 'dialog'}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity duration-150 ease-carbon"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-110 ease-carbon"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div aria-hidden="true" className="fixed inset-0 bg-black/60" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-stretch justify-center c-md:items-center c-md:p-4">
            <Transition.Child
              as={Fragment}
              enter="transition duration-240 ease-carbon-in"
              enterFrom="opacity-0 -translate-y-6"
              enterTo="opacity-100 translate-y-0"
              leave="transition duration-110 ease-carbon"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 -translate-y-6"
            >
              <Dialog.Panel
                data-menu=""
                className={clsx(
                  'flex w-full flex-col bg-layer-2 text-text-primary max-c-md:min-h-full c-md:max-h-[min(90vh,48rem)]',
                  WIDTH[size],
                  FIELD_ON_LAYER_02,
                )}
              >
                <ModalFrame {...frame} danger={danger} onClose={dismiss} titleAs={Dialog.Title} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default Modal
